/**
 * /system-admin/orgs/:orgId — org detail view.
 * Sections: info card, members card, feature toggles, actions.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { Building2, ChevronLeft } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { SystemAdminGuard } from "@/features/system-admin/components/system-admin-guard";
import { useSystemOrg } from "@/features/system-admin/api/orgs";
import { OrgDetailInfo } from "@/features/system-admin/components/orgs/org-detail-info";
import { OrgFeaturesPanel } from "@/features/system-admin/components/orgs/org-features-panel";
import { OrgActions } from "@/features/system-admin/components/orgs/org-actions";

export const Route = createFileRoute("/system-admin/orgs/$orgId")({
  component: SystemAdminOrgDetailPage,
});

function SystemAdminOrgDetailPage() {
  const t = useTranslations("system-admin.orgs");
  const { orgId } = Route.useParams();
  const { data, isLoading, isFetching, refetch } = useSystemOrg(orgId);

  const org = data?.data ?? null;

  return (
    <SystemAdminGuard>
      <div className="space-y-6">
        <div>
          <Link
            to="/system-admin/orgs"
            className="mb-3 inline-flex items-center gap-1 text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
          >
            <ChevronLeft className="size-4" aria-hidden />
            {t("detail.backToList")}
          </Link>

          <PageHeader
            icon={Building2}
            title={isLoading ? t("detail.loading") : (org?.name ?? t("detail.notFound.title"))}
            onReload={() => refetch()}
            isReloading={isFetching}
            actions={org ? <OrgActions org={org} /> : undefined}
          />
        </div>

        {!isLoading && !org && (
          <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-8 text-center">
            <p className="text-sm font-medium text-[var(--foreground)]">
              {t("detail.notFound.title")}
            </p>
            <p className="mt-1 text-sm text-[var(--muted-foreground)]">
              {t("detail.notFound.body")}
            </p>
          </div>
        )}

        {org && (
          <>
            <OrgDetailInfo org={org} />
            <OrgFeaturesPanel orgId={orgId} />
          </>
        )}
      </div>
    </SystemAdminGuard>
  );
}
